import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  Alert,
  CircularProgress,
  IconButton,
  Tooltip,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import UploadIcon from '@mui/icons-material/Upload';
import axios from 'axios';
import { API_BASE_URL } from '../../../config/api';

const MAX_FILE_SIZE_MB = 2;
const ACCEPTED_TYPES = ['image/png', 'image/jpeg', 'image/jpg', 'image/svg+xml', 'image/webp'];

const getImageUrl = (url: string) => {
  if (!url) return url;
  if (url.startsWith('http')) return url;
  const baseUrl = API_BASE_URL.replace('/api', '');
  if (url.startsWith('/')) return `${baseUrl}${url}`;
  return `${baseUrl}/${url}`;
};

const BrandingSettings: React.FC = () => {
  const [logoUrl, setLogoUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const fetchBranding = async () => {
      try {
        const token = localStorage.getItem('token');
        const businessId = localStorage.getItem('businessId');
        if (!token || !businessId) return;

        const res = await axios.get(`${API_BASE_URL}/businesses/${businessId}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setLogoUrl(res.data?.company_logo_url || null);
      } catch (err) {
        console.error('Failed to load branding settings', err);
      } finally {
        setLoading(false);
      }
    };
    fetchBranding();
  }, []);

  const saveLogo = async (url: string | null) => {
    const token = localStorage.getItem('token');
    const businessId = localStorage.getItem('businessId');
    if (!token || !businessId) return;

    await axios.put(
      `${API_BASE_URL}/businesses/${businessId}/branding`,
      { company_logo_url: url },
      { headers: { Authorization: `Bearer ${token}` } }
    );
  };

  const uploadFile = async (file: File) => {
    if (!ACCEPTED_TYPES.includes(file.type)) {
      setError('Please upload a PNG, JPEG, SVG, or WebP image.');
      return;
    }
    if (file.size > MAX_FILE_SIZE_MB * 1024 * 1024) {
      setError(`Image must be under ${MAX_FILE_SIZE_MB} MB.`);
      return;
    }

    setError(null);
    setSuccess(false);
    setUploading(true);

    try {
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('file', file);
      formData.append('folder', 'business-logos');

      const uploadRes = await axios.post(`${API_BASE_URL}/upload`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data',
        },
      });

      if (!uploadRes.data?.success) {
        throw new Error(uploadRes.data?.message || 'Upload failed.');
      }
      const uploadedUrl: string = uploadRes.data?.filePath || uploadRes.data?.url;
      if (!uploadedUrl) throw new Error('No URL returned from upload.');

      await saveLogo(uploadedUrl);
      setLogoUrl(uploadedUrl);
      setSuccess(true);
      setTimeout(() => setSuccess(false), 4000);
    } catch (err: any) {
      console.error('Logo upload failed', err);
      setError(err.response?.data?.message || err.message || 'Upload failed. Please try again.');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) uploadFile(file);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (!uploading) setDragOver(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(false);
    if (uploading) return;
    const file = e.dataTransfer.files?.[0];
    if (file) uploadFile(file);
  };

  const handleRemove = async () => {
    setError(null);
    setSuccess(false);
    setUploading(true);
    try {
      await saveLogo(null);
      setLogoUrl(null);
      setSuccess(true);
      setTimeout(() => setSuccess(false), 4000);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to remove logo.');
    } finally {
      setUploading(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Branding
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Upload your company logo to display on all generated PDFs (safety checks, loan agreements,
        delivery checks, etc.). Recommended size: at least 200 × 200 px. PNG or SVG with a
        transparent background works best.
      </Typography>

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(false)}>
          Branding updated successfully!
        </Alert>
      )}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Paper sx={{ p: 3 }}>
        {/* Preview */}
        <Typography variant="subtitle2" sx={{ mb: 1 }}>
          Company Logo
        </Typography>

        <Box
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          onClick={() => !uploading && fileInputRef.current?.click()}
          sx={{
            position: 'relative',
            display: 'flex',
            alignItems: 'center',
            gap: 3,
            mb: 3,
            p: 2,
            border: '2px dashed',
            borderColor: dragOver ? 'primary.main' : 'divider',
            borderRadius: 1,
            bgcolor: dragOver ? 'rgba(25, 118, 210, 0.06)' : 'grey.50',
            minHeight: 120,
            cursor: uploading ? 'default' : 'pointer',
            transition: 'border-color 0.15s, background-color 0.15s',
            '&:hover': uploading
              ? {}
              : { borderColor: 'primary.light', bgcolor: 'rgba(25, 118, 210, 0.04)' },
          }}
        >
          {dragOver && (
            <Box
              sx={{
                position: 'absolute',
                inset: 0,
                zIndex: 1,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: 'rgba(25, 118, 210, 0.1)',
                borderRadius: 1,
                pointerEvents: 'none',
              }}
            >
              <UploadIcon sx={{ fontSize: 36, color: 'primary.main', mb: 0.5 }} />
              <Typography variant="body2" color="primary" fontWeight={600}>
                Drop to upload
              </Typography>
            </Box>
          )}

          {uploading ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mx: 'auto' }}>
              <CircularProgress size={28} />
              <Typography variant="body2" color="text.secondary">
                Uploading…
              </Typography>
            </Box>
          ) : logoUrl ? (
            <>
              <Box
                component="img"
                src={getImageUrl(logoUrl)}
                alt="Company logo preview"
                onClick={(e) => e.stopPropagation()}
                sx={{
                  maxHeight: 80,
                  maxWidth: 200,
                  objectFit: 'contain',
                  borderRadius: 1,
                  bgcolor: 'white',
                  p: 0.5,
                  border: '1px solid',
                  borderColor: 'divider',
                }}
              />
              <Box sx={{ flex: 1 }} onClick={(e) => e.stopPropagation()}>
                <Typography variant="body2" color="text.secondary" sx={{ wordBreak: 'break-all' }}>
                  {logoUrl}
                </Typography>
                <Typography variant="caption" color="text.disabled" display="block" sx={{ mt: 0.5 }}>
                  Drag a new image here or click to replace
                </Typography>
              </Box>
              <Tooltip title="Remove logo">
                <IconButton
                  color="error"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleRemove();
                  }}
                  disabled={uploading}
                  size="small"
                >
                  <DeleteIcon />
                </IconButton>
              </Tooltip>
            </>
          ) : (
            <Box sx={{ textAlign: 'center', mx: 'auto', py: 1 }}>
              <UploadIcon sx={{ fontSize: 40, color: 'text.disabled', mb: 1 }} />
              <Typography variant="body2" color="text.secondary">
                Drag &amp; drop your logo here, or click to browse
              </Typography>
              <Typography variant="caption" color="text.disabled" display="block" sx={{ mt: 0.5 }}>
                PNG, JPEG, SVG, WebP — max {MAX_FILE_SIZE_MB} MB
              </Typography>
            </Box>
          )}
        </Box>

        {/* Upload button */}
        <input
          ref={fileInputRef}
          type="file"
          accept={ACCEPTED_TYPES.join(',')}
          style={{ display: 'none' }}
          onChange={handleFileChange}
        />
        <Button
          variant="outlined"
          startIcon={uploading ? <CircularProgress size={18} /> : <UploadIcon />}
          onClick={() => fileInputRef.current?.click()}
          disabled={uploading}
        >
          {uploading ? 'Uploading…' : logoUrl ? 'Replace Logo' : 'Upload Logo'}
        </Button>

        <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
          You can also drag and drop an image onto the area above.
        </Typography>
      </Paper>
    </Box>
  );
};

export default BrandingSettings;
